import { BrowserModule } from "@angular/platform-browser";
import {
  BrowserAnimationsModule,
  NoopAnimationsModule,
} from "@angular/platform-browser/animations";
import { NgModule, NO_ERRORS_SCHEMA } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { HttpClientModule, HTTP_INTERCEPTORS } from "@angular/common/http";
import { Routes, RouterModule, RouteReuseStrategy } from "@angular/router";

import {
  MDBSpinningPreloader,
  MDBBootstrapModulesPro,
  ToastModule,
  WavesModule,
  ButtonsModule,
} from "ng-uikit-pro-standard";
import { PlyrModule } from "ngx-plyr";
import { LottieCacheModule, LottieModule } from "ngx-lottie";
import { AngularFireModule } from "@angular/fire/compat";
import { AngularFireAuthModule } from "@angular/fire/compat/auth";
import { AngularFirestoreModule } from "@angular/fire/compat/firestore";
import { AngularFireStorageModule } from "@angular/fire/compat/storage";
import { DeviceDetectorModule } from "ngx-device-detector";
import { NgxSkeletonLoaderModule } from "ngx-skeleton-loader";
import { DBConfig, NgxIndexedDBModule } from "ngx-indexed-db";
import { ServiceWorkerModule } from "@angular/service-worker";

import { environment } from "src/environments/environment";
import { CustomDerectiveModule } from "./_clusters/custom-derective/custom-derective.module";
import { CairaAuthInterceptor } from "./pages/caira-ready/caira-auth.interceptor";
import { CairaRouteReuseStrategy } from "./pages/caira-ready/caira-route-reuse.strategy";
import {
  ZOOM_CONFIG,
  ZoomConfig,
  ZoomUserProvider,
  CairaZoomUserProvider,
} from "./pages/caira-ready/zoom/zoom.config";

import { AppComponent } from "./app.component";
import { DeviceGateComponent } from "./pages/device-gate/device-gate.component";
import {
  ZOOM_SDK_VERSION,
  ZOOM_SIGNATURE_ENDPOINT,
} from "./pages/caira-ready/zoom/zoom.credentials";

// Zoom Meeting SDK config. The credentials live in zoom/zoom.credentials.ts
// rather than in the environment files — see the header there, which is also
// where the (unchanged) exposure of the SDK secret is documented and where the
// backend-signing migration is written down.
const zoomConfig: ZoomConfig = {
  // No sdkKey/sdkSecret: they are fetched from zoom/sdk/credentials/ at join
  // time (ZoomCredentialsService) rather than compiled into the bundle.
  sdkVersion: ZOOM_SDK_VERSION,
  leaveUrl: "/home",
  // Empty today. Point this at a backend route and ZoomService stops signing
  // in the browser, which is the only thing that actually protects the secret.
  ...(ZOOM_SIGNATURE_ENDPOINT
    ? { signatureEndpoint: ZOOM_SIGNATURE_ENDPOINT }
    : {}),
};

export function playerFactory() {
  return import(/* webpackChunkName: 'lottie-web' */ "lottie-web");
}

// CAIRA is the whole app now: the root path loads the CAIRA feature module,
// whose own default child is the "Sign in with your phone" login page.
const routes: Routes = [
  {
    path: "",
    loadChildren: () =>
      import("./pages/caira-ready/caira-ready.module").then(
        (m) => m.CairaReadyModule
      ),
  },
  { path: "**", redirectTo: "" },
];

const dbConfig: DBConfig = {
  name: "cmacuation",
  version: 1,
  objectStoresMeta: [
    {
      store: "credentials",
      storeConfig: { keyPath: "data", autoIncrement: true },
      storeSchema: [
        { name: "name", keypath: "name", options: { unique: false } },
      ],
    },
  ],
};

@NgModule({
  // The device gate is declared HERE, not in the lazy CairaReadyModule: it has to
  // render before (and instead of) the router outlet that loads that module.
  declarations: [AppComponent, DeviceGateComponent],
  imports: [
    BrowserModule,
    CommonModule,
    BrowserAnimationsModule,
    NoopAnimationsModule,
    FormsModule,
    RouterModule.forRoot(routes),
    HttpClientModule,
    PlyrModule,
    LottieCacheModule,
    LottieModule.forRoot({ player: playerFactory }),
    MDBBootstrapModulesPro.forRoot(),
    ToastModule.forRoot(),
    WavesModule,
    ButtonsModule,
    AngularFireModule.initializeApp(environment.firebase),
    AngularFirestoreModule,
    AngularFireAuthModule,
    AngularFireStorageModule,
    DeviceDetectorModule.forRoot(),
    NgxSkeletonLoaderModule.forRoot(),
    NgxIndexedDBModule.forRoot(dbConfig),
    ServiceWorkerModule.register("ngsw-worker.js", {
      enabled: environment.production,
      registrationStrategy: "registerWhenStable:30000",
    }),
    CustomDerectiveModule,
  ],
  providers: [
    MDBSpinningPreloader,
    // Redirects to the CAIRA login when a CAIRA token expires (401/403 signature)
    // instead of leaving a blank screen. Pass-through for all non-CAIRA requests.
    { provide: HTTP_INTERCEPTORS, useClass: CairaAuthInterceptor, multi: true },
    // Cache the CAIRA / CAIRA Tracker tab components so switching between them is
    // instant and doesn't re-render/re-fetch (removes the tab-switch flicker).
    { provide: RouteReuseStrategy, useClass: CairaRouteReuseStrategy },
    // Zoom Meeting SDK: runtime config + the identity bridge that pre-fills the
    // student's name on the join page from the CAIRA session.
    { provide: ZOOM_CONFIG, useValue: zoomConfig },
    { provide: ZoomUserProvider, useClass: CairaZoomUserProvider },
  ],
  bootstrap: [AppComponent],
  schemas: [NO_ERRORS_SCHEMA],
})
export class AppModule {}
